import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Loader2, GraduationCap, CheckCircle2, Circle, RotateCcw } from "lucide-react";
import Navbar from "@/components/sections/ToolPageNavbar";
import Footer from "@/components/sections/Footer";
import api, { formatApiError } from "@/utils/api";
import { QuizStartForm } from "@/components/quiz/QuizStartForm";
import { QuizRunner } from "@/components/quiz/QuizRunner";
import { QuizResult } from "@/components/quiz/QuizResult";

const RetestInfo = ({ retest }) => (
  <div className="glass-card rounded-3xl p-6 md:p-8 mt-8" data-testid="quiz-retest-info">
    <div className="flex items-center gap-2 mb-2"><RotateCcw className="w-5 h-5 text-[#A855F7]" /><h3 className="font-display text-xl text-white">Want to take the test again?</h3></div>
    <p className="text-sm text-white/55 mb-4">You can take a new test (with new questions) after you download both of these reports:</p>
    <div className="flex flex-col sm:flex-row gap-3">
      {[["growth_done", "A new Growth Assessment report", "/tools/growth"], ["nutrition_done", "A new NIN-NutriGuide report", "/tools/nin-nutriguide"]].map(([k, l, to]) => (
        <Link key={k} to={to} className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-3 text-sm text-white/75 hover:border-white/25" data-testid={`quiz-retest-${k}`}>
          {retest[k] ? <CheckCircle2 className="w-4 h-4 text-[#4ECDC4]" /> : <Circle className="w-4 h-4 text-white/40" />} {l}
        </Link>
      ))}
    </div>
  </div>
);

const Header = () => (
  <div className="mb-10 max-w-3xl">
    <div className="badge-glass inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium mb-5">
      <GraduationCap className="w-3.5 h-3.5 text-[#FFE66D]" /> Free Parent Quiz
    </div>
    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-4" data-testid="quiz-title">
      How well do you know your <span className="text-[#4ECDC4]">child's growth?</span>
    </h1>
    <p className="text-white/60 text-base md:text-lg">10 quick questions on growth and nutrition. Get your certificate and a personal discount for your child's reports.</p>
  </div>
);

export default function ParentQuizPage() {
  const [status, setStatus] = useState(null);
  const [run, setRun] = useState(null);
  const [justDone, setJustDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/api/quiz/status").then(({ data }) => setStatus(data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.scrollTo(0, 0); }, [run]);

  const start = async (form = {}) => {
    setBusy(true);
    try { const { data } = await api.post("/api/quiz/start", form); setRun(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const onSubmitted = useCallback((data) => { setRun(null); setJustDone(data); load(); window.scrollTo({ top: 0 }); }, [load]);

  const resumeActive = () => start();
  const last = status?.attempts?.[0];
  const code = status?.personal_code;

  let body;
  if (!status) body = <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 text-[#4ECDC4] animate-spin" /></div>;
  else if (run) body = <QuizRunner attempt={run.attempt} questions={run.questions} initialAnswers={run.answers} serverNow={run.server_now} onSubmitted={onSubmitted} />;
  else if (justDone) body = <QuizResult attempt={justDone.attempt} code={justDone.personal_code} price={justDone.price} uses={code?.uses || { growth: false, nutrition: false }} />;
  else if (status.active_attempt) body = (
    <div className="glass-card rounded-3xl p-8 text-center" data-testid="quiz-resume">
      <p className="text-white/70 mb-4">You have a test in progress.</p>
      <button onClick={resumeActive} disabled={busy} className="btn-coral px-6 py-3 rounded-full font-semibold" data-testid="quiz-resume-btn">Continue my test</button>
    </div>
  );
  else if (status.can_start) body = <QuizStartForm profile={status.profile} onStart={start} busy={busy} />;
  else body = (
    <>
      <QuizResult attempt={last} code={code?.code} price={code?.price} uses={code?.uses} showSteps />
      <RetestInfo retest={status.retest} />
    </>
  );

  return (
    <div className="min-h-screen bg-[#0A1029] text-white" data-testid="parent-quiz-page">
      <Navbar />
      <main className="relative px-6 pt-32 md:pt-40 pb-24">
        <div className="absolute -top-20 right-0 w-[420px] h-[420px] rounded-full bg-[#4ECDC4]/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto">
          {!run && <Header />}
          {body}
        </div>
      </main>
      <Footer />
      <Toaster position="top-right" />
    </div>
  );
}
